/**
 * OFFLINE-01 (Seth 2026-09-29, via the Gemini architecture review): the
 * Outbox — the one genuinely right idea in that playbook, built native to
 * OUR stack (React + Vite + Capacitor), zero new dependencies.
 *
 * Techs work in dead zones (basements, mechanical rooms). Any mutation that
 * must not be lost writes here FIRST when the network is gone: it lands in
 * localStorage, the UI updates optimistically, and a background flusher
 * replays it against Supabase the moment signal returns (online event, app
 * foreground, or the 25s tick). Executors are registered per kind by the
 * feature that owns the write, and replay re-reads server state first, so a
 * queue built up offline applies cleanly in order.
 *
 * Deliberately NOT WatermelonDB/SQLite: our offline unit is "a tech's day",
 * not a synced relational store — a queue plus a day snapshot covers the
 * real failure (no signal for minutes-to-hours) without forking the app's
 * data layer.
 */
import { useSyncExternalStore } from "react";

export interface OutboxItem {
  id: string;
  kind: string;
  payload: unknown;
  queued_at: string;
  tries: number;
}

const KEY = "pegasus.outbox.v1";

function load(): OutboxItem[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(v) ? (v as OutboxItem[]) : [];
  } catch {
    return [];
  }
}
function save(items: OutboxItem[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // storage unavailable — the optimistic UI still worked; nothing else to do
  }
}

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

type Executor = (payload: unknown) => Promise<void>;
const executors = new Map<string, Executor>();

/** A feature registers how its queued writes replay (module scope, once). */
export function registerOutboxExecutor(kind: string, run: Executor) {
  executors.set(kind, run);
}

export function enqueueOutbox(kind: string, payload: unknown) {
  const items = load();
  items.push({
    id: typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
    kind,
    payload,
    queued_at: new Date().toISOString(),
    tries: 0,
  });
  save(items);
  emit();
  void flushOutbox(); // in case we're actually online (flaky-signal writes)
}

let flushing = false;
export async function flushOutbox() {
  if (flushing) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  flushing = true;
  try {
    // Strictly in order — a later status change depends on an earlier one.
    for (;;) {
      const items = load();
      const it = items[0];
      if (!it) break;
      const run = executors.get(it.kind);
      if (!run) break; // owner module not loaded yet — try again later
      try {
        await run(it.payload);
        save(load().filter((x) => x.id !== it.id));
        emit();
      } catch {
        const cur = load();
        if (cur[0]?.id === it.id) {
          cur[0].tries += 1;
          save(cur);
        }
        emit();
        break; // stop at the first failure; the next tick retries
      }
    }
  } finally {
    flushing = false;
  }
}

// Background wiring — module-scope, guarded.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    emit();
    void flushOutbox();
  });
  window.addEventListener("offline", emit);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void flushOutbox();
  });
  window.setInterval(() => void flushOutbox(), 25_000);
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** Live outbox state for UI chips: pending count + whether we're offline. */
export function useOutbox(): { pending: number; offline: boolean } {
  const pending = useSyncExternalStore(subscribe, () => load().length, () => 0);
  const offline = useSyncExternalStore(
    subscribe,
    () => (typeof navigator !== "undefined" ? !navigator.onLine : false),
    () => false,
  );
  return { pending, offline };
}
